import { UniversalTrackerBlueprintMin, UtrValueType, TableColumnType } from '@g17eco/types/universalTracker';
import {
  canAddTarget,
  isNumericTableColumnType,
  isSingleRowTableType,
  isTableGroupAggregation,
} from '@utils/universalTracker';
import { ChartSubType } from '@g17eco/types/insight-custom-dashboard';
import { isBreakoutCapableSubType } from '../utils/dashboard-utils';

const isBreakoutCapable = (question: UniversalTrackerBlueprintMin, chartSubType: ChartSubType | undefined): boolean =>
  isBreakoutCapableSubType(chartSubType) && isTableGroupAggregation(question);

// types that doesn't include value list, table.
export const SIMPLE_TYPES: string[] = [
  UtrValueType.Number,
  UtrValueType.Percentage,
  UtrValueType.Date,
  UtrValueType.Text,
  UtrValueType.Sample,
];

export enum QuestionFilterType {
  Pack = 'pack',
  Subpack = 'subPack',
  Question = 'question',
  Input = 'input',
}

// chartSubType is optional: text-widget / table-input callers omit it; chart callers
// pass it through.
export type ValidatingFunc = (
  question: UniversalTrackerBlueprintMin,
  valueListCode?: string,
  chartSubType?: ChartSubType,
) => {
  isAllowed: boolean;
  disableReason?: string;
};

// Grouped-table metrics aren't targetable but ARE renderable as Bar/Line breakouts —
// surface them as supported when the caller's chart subtype is breakout-capable.
export const checkIsNumericInput = (
  question: UniversalTrackerBlueprintMin,
  valueListCode?: string,
  chartSubType?: ChartSubType,
): ReturnType<ValidatingFunc> => {
  if (!valueListCode) {
    const isSupported = canAddTarget(question) || isBreakoutCapable(question, chartSubType);
    return { isAllowed: true, disableReason: isSupported ? undefined : 'Not supported' };
  }

  const column = question.valueValidation?.table?.columns?.find((c) => c.code === valueListCode);
  if (!column) {
    return { isAllowed: false };
  }

  const isSupported = isNumericTableColumnType(column.type);
  return { isAllowed: true, disableReason: isSupported ? undefined : 'Not supported' };
};

/**
 * Validates whether a question/column is selectable for text widgets.
 *
 * Called in two stages during the hierarchical filter selection:
 * 1. Question level (no valueListCode) — determines if the question appears in the dropdown.
 *    Single-row tables return allowed here to let the user drill into column selection.
 * 2. Column level (with valueListCode) — validates the specific column type,
 *    only allowing TableColumnType.Text columns.
 *
 * Return values:
 * - `{ isAllowed: true }` — fully selectable
 * - `{ isAllowed: true, disableReason }` — visible but grayed out with tooltip
 * - `{ isAllowed: false }` — hidden entirely (e.g. column code not found in schema)
 */
export const checkIsTextInput: ValidatingFunc = (question, valueListCode) => {
  if (question.valueType === UtrValueType.Text) {
    return { isAllowed: true };
  }

  if (question.valueType !== UtrValueType.Table) {
    return { isAllowed: true, disableReason: 'Not supported' };
  }

  if (!isSingleRowTableType(question)) {
    return { isAllowed: true, disableReason: 'Multi row table not supported' };
  }

  if (!valueListCode) {
    return { isAllowed: true };
  }

  const column = question.valueValidation?.table?.columns?.find((c) => c.code === valueListCode);
  if (!column) {
    return { isAllowed: false };
  }

  const isSupported = column.type === TableColumnType.Text;
  return { isAllowed: true, disableReason: isSupported ? undefined : 'Not supported' };
};
